import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LanguageCode } from '@repo/domain';
import { ArrowRightFromBracketIcon, CalendarMinusIcon, PlanLocationIcon, UserIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { useHasActiveReservations, useUser } from '@repo/domain-service';
import { DotSeparator } from '@repo/ui';

import { AuthModal, logout, selectStatus, useAuthStore } from '@features/auth';

import LanguageDropdown from '@shared/components/language/LanguageDropdown';
import { useOnClickOutside } from '@shared/hooks/useOnClickOutside';
import useSwitchLanguage from '@shared/hooks/useSwitchLanguage';
import { useLanguageStore } from '@shared/stores/useLanguageStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

export default function HamburgerMenu() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoginOpen, setIsLoginOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const testId = TestId.root('hamburgerMenu');
  const isLoggedIn = useAuthStore(selectStatus) === 'authenticated';
  const hasSession = useAuthStore(selectStatus) !== 'unauthenticated';
  const { hasActiveReservations } = useHasActiveReservations(hasSession);
  const { user } = useUser();
  const language = useLanguageStore((state) => state.language);
  const { switchLanguage } = useSwitchLanguage();

  const closeMenu = useCallback(() => setIsOpen(false), []);
  useOnClickOutside(menuRef, closeMenu, isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (isLoginOpen && isLoggedIn) {
      navigate(ROUTE_PATHS[RouteName.ACCOUNT]);
    }
  }, [isLoginOpen, isLoggedIn, navigate]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleNavigation = (callback: () => void) => {
    setIsOpen(false);
    callback();
  };

  const handleReservationsClick = () => {
    handleNavigation(() => navigate(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS]));
  };

  const handleMapClick = () => {
    handleNavigation(() => navigate(ROUTE_PATHS[RouteName.MAP]));
  };

  const handleAccountClick = () => {
    handleNavigation(() => navigate(ROUTE_PATHS[RouteName.ACCOUNT]));
  };

  const handleLogout = () => {
    logout();
    handleNavigation(() => navigate(ROUTE_PATHS[RouteName.MAP]));
  };

  const handleLogin = () => {
    setIsOpen(false);
    setIsLoginOpen(true);
  };

  const handleLanguageChange = (next: LanguageCode) => {
    void switchLanguage(next);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        className="hover:bg-brand-softer relative flex size-12 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg"
        onClick={handleToggle}
        aria-label={tr('hamburger_menu.aria_label')}
        aria-expanded={isOpen}
        data-testid={testId.append('toggle')}
      >
        {hasActiveReservations && <DotSeparator color="bg-danger" size="md" className="absolute top-2 left-9" />}
        <span className="border-brand-strong block h-0.5 w-6 rounded-full border" />
        <span className="border-brand-strong block h-0.5 w-6 rounded-full border" />
        <span className="border-brand-strong block h-0.5 w-6 rounded-full border" />
      </button>

      {isOpen && (
        <div
          className="border-stroke-base-subtle z-dropdown absolute top-full right-0 mt-2 rounded-lg border bg-white shadow-lg"
          data-testid={testId}
        >
          {isLoggedIn && user && (
            <>
              <div className="bg-stroke-base-strong h-px w-full" />
              <div className="flex flex-col gap-0.5 py-3 pr-5 pl-4" data-testid={testId.append('user')}>
                <span className="text-heading text-base font-semibold" data-testid={testId.append('userName')}>
                  {user.name}
                </span>
                <span className="text-body-subtle text-sm" data-testid={testId.append('userEmail')}>
                  {user.email}
                </span>
              </div>
              <div className="bg-stroke-base-strong h-px w-full" />
            </>
          )}

          <nav className="flex flex-col">
            <button
              className="text-heading hover:bg-soft-orange flex cursor-pointer items-center gap-2 py-3 pr-5 pl-4 text-sm"
              onClick={handleReservationsClick}
              data-testid={testId.append('reservationsButton')}
            >
              <CalendarMinusIcon className="text-body" />
              {tr('bottom_bar.reservations')}
              {hasActiveReservations && <DotSeparator color="bg-danger" size="md" />}
            </button>
            <button
              className="text-heading hover:bg-soft-orange flex cursor-pointer items-center gap-2 py-3 pr-5 pl-4 text-sm"
              onClick={handleMapClick}
              data-testid={testId.append('parkButton')}
            >
              <PlanLocationIcon className="text-body" />
              {tr('bottom_bar.park')}
            </button>
            <button
              className="text-heading hover:bg-soft-orange flex cursor-pointer items-center gap-2 py-3 pr-5 pl-4 text-sm"
              onClick={handleAccountClick}
              data-testid={testId.append('accountButton')}
            >
              <UserIcon className="text-body" />
              {tr('bottom_bar.account')}
            </button>

            <div className="bg-brand-softer pr-5 pl-4" data-testid={testId.append('language')}>
              <div className="bg-stroke-base-strong h-px w-full" />
              <LanguageDropdown
                value={language}
                onChange={handleLanguageChange}
                testId={testId.append('languageDropdown')}
                enablePortal={false}
                variant="inline"
              />
              <div className="bg-stroke-base-strong h-px w-full" />
            </div>

            {isLoggedIn ? (
              <button
                className="text-body hover:bg-soft-orange flex cursor-pointer items-center gap-2 py-3 pr-5 pl-4 text-sm"
                onClick={handleLogout}
                data-testid={testId.append('logoutButton')}
              >
                <ArrowRightFromBracketIcon size={16} className="text-body mr-0.5 ml-1" />
                {tr('bottom_bar.logout')}
              </button>
            ) : (
              <button
                className="text-heading hover:bg-soft-orange flex cursor-pointer items-center gap-2 py-3 pr-5 pl-4 text-sm"
                onClick={handleLogin}
                data-testid={testId.append('loginButton')}
              >
                <ArrowRightFromBracketIcon size={16} className="text-body mr-0.5 ml-1" />
                {tr('bottom_bar.login')}
              </button>
            )}
          </nav>
        </div>
      )}
      {isLoginOpen && !isLoggedIn && <AuthModal onOpenChange={setIsLoginOpen} />}
    </div>
  );
}
